// RECEIVED is only ever set by the yard validating a container (Delivery
// validation > Receipts), which books its stock in.
export const SHIPMENT_STATUSES = ['BOOKED', 'IN TRANSIT', 'ARRIVED', 'CLEARED', 'DELIVERED', 'RECEIVED', 'CANCELLED'];

// One colour family per state, picked so no two look alike at a glance:
// slate, sky blue, orange, violet, lime, emerald, red. Everything showing a
// shipment or container state uses these - dots, badges, bars, summaries.
export const SHIPMENT_STATE_COLORS: Record<string, { dot: string; badge: string; soft: string; text: string }> = {
  BOOKED: { dot: 'bg-slate-400', badge: 'bg-slate-100 border border-slate-400 text-slate-700', soft: 'bg-slate-50 text-slate-700 border-slate-200', text: 'text-slate-600' },
  'IN TRANSIT': { dot: 'bg-sky-500', badge: 'bg-sky-100 border border-sky-500 text-sky-800', soft: 'bg-sky-50 text-sky-800 border-sky-200', text: 'text-sky-700' },
  ARRIVED: { dot: 'bg-orange-500', badge: 'bg-orange-100 border border-orange-500 text-orange-800', soft: 'bg-orange-50 text-orange-800 border-orange-200', text: 'text-orange-700' },
  CLEARED: { dot: 'bg-violet-500', badge: 'bg-violet-100 border border-violet-500 text-violet-800', soft: 'bg-violet-50 text-violet-800 border-violet-200', text: 'text-violet-700' },
  DELIVERED: { dot: 'bg-lime-500', badge: 'bg-lime-100 border border-lime-600 text-lime-800', soft: 'bg-lime-50 text-lime-800 border-lime-300', text: 'text-lime-700' },
  RECEIVED: { dot: 'bg-emerald-600', badge: 'bg-emerald-100 border border-emerald-600 text-emerald-800', soft: 'bg-emerald-50 text-emerald-800 border-emerald-300', text: 'text-emerald-700' },
  CANCELLED: { dot: 'bg-red-500', badge: 'bg-red-100 border border-red-500 text-red-700', soft: 'bg-red-50 text-red-700 border-red-200', text: 'text-red-700' },
};

const FALLBACK = SHIPMENT_STATE_COLORS['BOOKED'];

// Badge (pill with border).
export function shipmentStatusClass(status: string): string {
  return (SHIPMENT_STATE_COLORS[status] ?? FALLBACK).badge;
}

// Dot / bar segment / row accent.
export function shipmentStatusDot(status: string): string {
  return (SHIPMENT_STATE_COLORS[status] ?? { dot: 'bg-gray-300' }).dot;
}

// Light chip (the container state button on the shipment form).
export function shipmentStatusSoft(status: string): string {
  return (SHIPMENT_STATE_COLORS[status] ?? FALLBACK).soft;
}

// Coloured text (summaries like "2/4 cleared").
export function shipmentStatusText(status: string): string {
  return (SHIPMENT_STATE_COLORS[status] ?? FALLBACK).text;
}

// ETA colouring for shipments still on their way: overdue red, due today
// amber, later green.
export function etaClass(eta: string | null, status: string): string {
  if (!eta || ['DELIVERED', 'RECEIVED', 'CANCELLED', 'CLEARED'].includes(status)) return 'text-neutral-600';
  const due = new Date(eta);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due.getTime() < today.getTime()) return 'text-red-600 font-semibold';
  if (due.getTime() === today.getTime()) return 'text-amber-600 font-semibold';
  return 'text-green-700';
}
